import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

import { axiosClient } from '../../../core'
import { requestStatuses } from '../../../core/constants'

const fetchRequestsCount = createAsyncThunk(
  'fetchRequestsCount',
  async () => {
    const requests = await axiosClient.get('/service-requests/count/status/')
    return { ...requests.data, ALL: Object.values(requests.data).reduce((acc, item) => acc+=item, 0)}
  }
)

const fetchRequests = createAsyncThunk(
  'fetchRequests',
  async ({ filters }) => {
    const requests = await axiosClient.get('/service-requests/', {
      params: {
        ...filters,
      }
    })

    return requests.data
  }
)

// Kanban shows every status at once as columns, so it needs the full set
// rather than the single-status page the list view's pagination uses -
// kept as separate state (below) so the two views don't fight over paging.
const fetchRequestsKanban = createAsyncThunk(
  'fetchRequestsKanban',
  async () => {
    const requests = await axiosClient.get('/service-requests/', {
      params: {
        page_size: 200,
      }
    })

    return requests.data
  }
)

const updateRequestStatus = createAsyncThunk(
  'updateRequestStatus',
  async ({ id, status }) => {
    const request = await axiosClient.patch(`/service-requests/${id}`, { status })

    return request.data
  }
)

const fetchRequest = createAsyncThunk(
  'fetchRequest',
  async ({ id }) => {
    const request = await axiosClient.get(`/service-requests/${id}`)

    return request.data
  }
)

const fetchRequestHistory = createAsyncThunk(
  'fetchRequestHistory',
  async ({ id }) => {
    const request = await axiosClient.get(`/service-requests/${id}/history/`)

    return request.data.results
  }
)

const initialState = {
  filter: requestStatuses.ALL,
  requests: {
    items: [],
    page: 1,
    page_size: 10,
    count: 0,
    order: '',
    direction: ''
  },
  request: null,
  requestsCount: null,
  requestHistory: null,
  kanban: {
    items: [],
    loading: false
  }
}

export const slice = createSlice({
  name: 'requests',

  initialState,

  reducers: {
    setRequestsFilter(state, { payload }) {
      state.filter = payload
    },
    setRequests(state, { payload }) {
      state.requests = { ...state.requests, ...payload }
    },
    setRequest(state, { payload }) {
      state.request = payload
    },
  },

  extraReducers: (builder) => {
    builder.addCase(fetchRequests.fulfilled, (state, { payload: { count, results } }) => {
      state.requests = {
        ...state.requests,
        count,
        items: results
      }
    })

    builder.addCase(fetchRequest.fulfilled, (state, { payload }) => {
      state.request = payload;
    })

    builder.addCase(fetchRequestHistory.fulfilled, (state, { payload }) => {
      state.requestHistory = payload;
    })

    builder.addCase(fetchRequestsCount.fulfilled, (state, { payload }) => {
      state.requestsCount = payload;
    })

    builder.addCase(fetchRequestsKanban.pending, (state) => {
      state.kanban.loading = true
    })
    builder.addCase(fetchRequestsKanban.fulfilled, (state, { payload: { results } }) => {
      state.kanban.loading = false
      state.kanban.items = results
    })
    builder.addCase(fetchRequestsKanban.rejected, (state) => {
      state.kanban.loading = false
    })

    builder.addCase(updateRequestStatus.fulfilled, (state, { payload }) => {
      const item = state.kanban.items.find((i) => i.id === payload.id)
      if (item) {
        item.status = payload.status
      }
    })
  }
})

const {
  setRequestsFilter,
  setRequest,
  setRequests
} = slice.actions

export {
  setRequestsFilter,
  fetchRequests,
  fetchRequestsKanban,
  updateRequestStatus,
  fetchRequestHistory,
  setRequest,
  setRequests,
  fetchRequestsCount,
  fetchRequest
}

export default slice.reducer;
